import {
  Box,
  Button,
  Card,
  CardActions,
  CardMedia,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";

import { Link } from "react-router-dom";

import { useEffect } from "react";

import seasonalPic1 from "../oriole_2.jpg";
import seasonalPic2 from "../pexels-yusuf-13009416.jpg";
import seasonalPic3 from "../premium_serviced_site_pictures/premium_3.webp";
import seasonalPic4 from "../premium_serviced_site_pictures/premium_30.jpg";
import seasonalPic5 from "../premium_serviced_site_pictures/premium_37_pic2.jpg";

import { constants } from "../constants";

export const ValueSeasonSeasonal = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const photos = [seasonalPic2, seasonalPic3, seasonalPic4, seasonalPic5];

  const seasonalDates = constants.seasonalDates[2027];

  const springPrice = constants.seasonalRates.spring;
  const fallPrice = constants.seasonalRates.fall;
  const springFallPrice = constants.seasonalRates.springFall;

  // Premium Serviced value-season nightly rate
  const valueSeasonPremiumRate =
    constants.depositRates[2027].value.rates["Premium Serviced"];

  const formatDate = (dateString) => {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  };

  // Calculate the number of nights between two dates.
  // The end date is the checkout date, so it is NOT included
  // in the number of nights.
  const calculateNights = (startDate, endDate) => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);

    return Math.round((end - start) / (1000 * 60 * 60 * 24));
  };

  // Number of nights in each seasonal period
  const springNights = calculateNights(
    seasonalDates.springStart,
    seasonalDates.springEnd,
  );

  const fallNights = calculateNights(
    seasonalDates.fallStart,
    seasonalDates.fallEnd,
  );

  // What the same number of nights would cost using the
  // regular Premium Serviced value-season nightly rate.
  const springRegularCost = springNights * valueSeasonPremiumRate;
  const fallRegularCost = fallNights * valueSeasonPremiumRate;

  // Savings compared with paying the regular nightly rate.
  const springSavings = springRegularCost - springPrice;
  const fallSavings = fallRegularCost - fallPrice;

  // Spring & Fall savings are the combined savings from
  // the two separate seasonal periods.
  const springFallRegularCost = springRegularCost + fallRegularCost;
  const springFallSavings = springFallRegularCost - springFallPrice;

  return (
    <div className="page">
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{ mt: 2, fontWeight: "bold" }}
        >
          Spring / Fall Seasonal Sites
        </Typography>

        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Enjoy the best of camping with a seasonal site during the peaceful
          spring and fall seasons.
        </Typography>

        {/* Hero Image */}
        <Card
          elevation={4}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            mb: 4,
          }}
        >
          <CardMedia
            component="img"
            image={seasonalPic1}
            alt="Seasonal campsite at Lands End Park"
            sx={{
              height: { xs: 250, sm: 400, md: 500 },
              objectFit: "cover",
            }}
          />
        </Card>

        {/* Description */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Enjoy a Seasonal Site
          </Typography>

          <Typography paragraph>
            Looking for a place to enjoy the warmer days of spring or the
            beautiful colours and peaceful atmosphere of fall? Our spring and
            fall seasonal sites offer an affordable way to enjoy more time at
            the park during these quieter parts of the camping season.
          </Typography>

          <Typography paragraph>
            Instead of packing up your campsite every weekend, you can leave
            your trailer or camping setup at the park throughout your seasonal
            stay and enjoy the freedom of coming and going whenever you'd like.
          </Typography>

          <Typography paragraph>
            Seasonal sites are available for the spring, the fall, or both.
            Whether you want an early start to your camping season, a relaxing
            fall getaway, or both, there is an option to suit your plans.
          </Typography>
        </Paper>

        {/* Seasonal Pricing */}
        <Typography variant="h4" align="center" gutterBottom>
          Seasonal Rates
        </Typography>

        <Grid container spacing={3} sx={{ mb: 10 }}>
          {/* Spring */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                pt: 3,
                px: 3,
                pb: 1,
                height: "100%",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Spring
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {formatDate(seasonalDates.springStart)} –{" "}
                {formatDate(seasonalDates.springEnd)}
              </Typography>

              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ${springPrice.toLocaleString()}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Total savings of ${springSavings.toLocaleString()} compared to
                paying nightly rates
              </Typography>
            </Paper>
          </Grid>

          {/* Fall */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                pt: 3,
                px: 3,
                pb: 1,
                height: "100%",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Fall
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {formatDate(seasonalDates.fallStart)} –{" "}
                {formatDate(seasonalDates.fallEnd)}
              </Typography>

              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ${fallPrice.toLocaleString()}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Total savings of ${fallSavings.toLocaleString()} compared to
                paying nightly rates
              </Typography>
            </Paper>
          </Grid>

          {/* Spring & Fall */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={3}
              sx={{
                pt: 3,
                px: 3,
                pb: 1,
                height: "100%",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Spring & Fall
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {formatDate(seasonalDates.springStart)} –{" "}
                {formatDate(seasonalDates.springEnd)} and{" "}
                {formatDate(seasonalDates.fallStart)} –{" "}
                {formatDate(seasonalDates.fallEnd)}
              </Typography>

              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ${springFallPrice.toLocaleString()}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Total savings of ${springFallSavings.toLocaleString()} compared
                to paying nightly rates
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 5 }}>
          {photos.map((photo, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card elevation={3}>
                <CardMedia
                  component="img"
                  image={photo}
                  alt={`Seasonal Camping ${index + 2}`}
                  sx={{
                    height: 300,
                    objectFit: "cover",
                  }}
                />
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Good to Know */}
        <Paper elevation={3} sx={{ p: 4, mb: 5 }}>
          <Typography variant="h5" gutterBottom>
            Good to Know
          </Typography>

          <Box sx={{ pl: 2 }}>
            <Typography paragraph>
              • Seasonal sites are available for the spring, fall, or both
              seasons.
            </Typography>

            <Typography paragraph>
              • Spring seasonal sites run from{" "}
              {formatDate(seasonalDates.springStart)} through{" "}
              {formatDate(seasonalDates.springEnd)}.
            </Typography>

            <Typography paragraph>
              • Fall seasonal sites run from{" "}
              {formatDate(seasonalDates.fallStart)} through{" "}
              {formatDate(seasonalDates.fallEnd)}.
            </Typography>

            <Typography paragraph>
              • The Spring & Fall seasonal option allows you to enjoy both
              seasons for a combined rate of ${springFallPrice.toLocaleString()}
              .
            </Typography>

            <Typography paragraph>
              • Seasonal camping is a great option for guests who want to enjoy
              the park frequently without having to set up and take down their
              campsite for every visit.
            </Typography>

            <Typography paragraph>
              • Spring and fall are generally quieter times at the park, making
              them ideal for relaxing weekends and enjoying the outdoors.
            </Typography>
          </Box>
        </Paper>

        {/* Call to Action */}
        <Box
          sx={{
            textAlign: "center",
            mb: 5,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Interested in a Spring / Fall Seasonal Site?
          </Typography>

          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
          >
            Contact Us
          </Button>
        </Box>
      </Container>
    </div>
  );
};
